import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import axios from 'axios';
// import { REACT_APP_API_BASE_URL } from '../config';
import { getApiUrl, POS_API_BASE_URL } from '../utils/apiHelper';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loginType, setLoginType] = useState(
    localStorage.getItem('loginType') || 'guts'
  );
  const [posToken, setPosToken] = useState(
    localStorage.getItem('posToken') || null
  );

  useEffect(() => {
    if (loginType === 'guts') {
      // Firebase auth for GUTS
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        try {
          if (firebaseUser) {
            setUser(firebaseUser);
            
            const token = await firebaseUser.getIdToken();
            const response = await axios.get(
              getApiUrl('guts', '/admin_authentication'),
              {
                headers: {
                  Authorization: `Bearer ${token}`
                }
              }
            );
            setIsAdmin(response.data.exists);
          } else {
            setUser(null);
            setIsAdmin(false);
          }
        } catch (error) {
          console.error('Admin authentication failed:', error);
          setIsAdmin(false);
        } finally {
          setLoading(false);
        }
      });
      return () => unsubscribe();
    } else if (loginType === 'pos' && posToken) {
      // POS token validation
      const validatePosToken = async () => {
        try {
          const response = await axios.get(
            `${POS_API_BASE_URL}/pos/myStore?pos_region=giva_in`,
            {
              headers: {
                'xaccesstoken': posToken,
                'accept': 'application/json',
                'pos_region': 'giva_in'
              }
            }
          );
          
          if (response.data.status === 200) {
            setUser({ email: response.data.data?.email || 'POS User', loginType: 'pos' });
            setIsAdmin(false); // POS users are not admins
          } else {
            throw new Error('Invalid POS token');
          }
        } catch (error) {
          console.error('POS token validation failed:', error);
          setPosToken(null);
          localStorage.removeItem('posToken');
          setUser(null);
          setIsAdmin(false);
        } finally {
          setLoading(false);
        }
      };
      validatePosToken();
    } else {
      setLoading(false);
    }
  }, [loginType, posToken]);

  const value = React.useMemo(() => ({
    user,
    loading,
    isAdmin,
    loginType,
    posToken,
    setLoginType: (type) => {
      localStorage.setItem('loginType', type);
      setLoginType(type);
    },
    setPosToken: (token) => {
      if (token) {
        localStorage.setItem('posToken', token);
      } else {
        localStorage.removeItem('posToken');
      }
      setPosToken(token);
    }
  }), [user, loading, isAdmin, loginType, posToken]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const getAuthHeaders = async () => {
  const loginType = localStorage.getItem('loginType') || 'guts';
  
  if (loginType === 'guts') {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const token = await user.getIdToken();
    console.log("GUTS token fetched", token);
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  } else {
    const posToken = localStorage.getItem('posToken');
    if (!posToken) throw new Error("POS token not found");
    console.log("POS token fetched", posToken);
    return {
      'xaccesstoken': posToken,
      'pos_region': 'giva_in',
      'accept': 'application/json',
      'Content-Type': 'application/json',
    };
  }
};

export const useAuth = () => useContext(AuthContext);